import { ALL_COUNTRIES_WITH_USA_FIRST } from "$lib/util/country"
import { isValidTaxNumber } from "$lib/yup/personal-info/tax-information"
import { phone } from "$lib/yup/util/phone-number"
import {
  MAX_SHARE,
  MIN_SHARE,
  entityBeneficialOwnerSchema,
} from "@tastyworks/tastyworks-api/validate"
import type { Alpha3Code } from "i18n-iso-countries/index"
import {
  array,
  boolean,
  mixed,
  number,
  object,
  string,
  type InferType,
} from "yup"

export const individualOwnerSchema = object({
  individualHasShare: boolean().required(),
  individualPercentageShare: number().when(["individualHasShare"], {
    is: true,
    then: (schema) => schema.required().min(MIN_SHARE).max(MAX_SHARE),
    otherwise: (schema) => schema.notRequired(),
  }),
})

export const individualOwnerOtherSchema = object({
  individualOtherHasShare: boolean().required(),
})

export const otherEntityOwnershipSchema = object({
  entityOtherHasShare: boolean().required(),
})

export const beneficialOwnerNameSchema = entityBeneficialOwnerSchema.pick([
  "firstName",
  "middleName",
  "lastName",
])

export const beneficialOwnerDetailsSchema = entityBeneficialOwnerSchema
  .pick(["email", "birthDate", "ownershipPercent"])
  .concat(
    object({
      // Make required when backend ready - ATP-14144
      phoneNumber: phone().optional(),
    })
  )

export const beneficialOwnerTaxInfoSchema = object({
  citizenshipCountry: mixed<Alpha3Code>()
    .required()
    .oneOf(ALL_COUNTRIES_WITH_USA_FIRST),
  residesInUS: boolean().required(),
  taxNumber: string().when("residesInUS", {
    is: true,
    then: (schema) =>
      schema
        .transform((value) => value?.replaceAll("-", ""))
        .length(9)
        .test(
          "invalid-tax-number",
          "yup.common.taxInformation.invalidTaxNumber",
          isValidTaxNumber
        )
        .required(),
    otherwise: (schema) => schema.max(40).required(),
  }),
  useCustomerTaxNumber: boolean().optional(),
})

export const beneficialOwnerAddressSchema = entityBeneficialOwnerSchema.pick([
  "address",
])

export const beneficialOwnerSchema = beneficialOwnerNameSchema
  .concat(beneficialOwnerDetailsSchema)
  .concat(beneficialOwnerTaxInfoSchema)
  .concat(beneficialOwnerAddressSchema)

export type BeneficialOwnerData = InferType<typeof beneficialOwnerSchema>

export const beneficialOwnersTableSchema = object({
  applicantPercentageShare: number().default(0),
  individualHasShare: boolean().default(false),
  shouldShowOwnerForm: boolean().default(false),
  beneficialOwners: array()
    .of(beneficialOwnerSchema)
    .ensure()
    .when(["shouldShowOwnerForm"], {
      is: false,
      then: (schema) =>
        schema
          // there is no separate field for applicantPercentageShare on beneficial-owners-table, so we
          // need to validate it along with beneficialOwners, otherwise editing percentage for applicant
          // doesn't get validated and the form can continue with invalid data
          .test({
            name: "applicant-min-share",
            test: function () {
              if (!this.parent.individualHasShare) return true
              const applicantPercentage =
                this.parent.applicantPercentageShare ?? 0
              if (applicantPercentage >= MIN_SHARE) return true
              return this.createError({
                message: {
                  key: "yup.common.number.min",
                  values: { min: MIN_SHARE },
                },
              })
            },
          })
          .test({
            name: "max-100%",
            message: "yup.common.number.maxOneHundredPercent",
            test: function (value) {
              if (!value || value.length === 0) {
                return true
              }

              const ownershipTotal = value.reduce(
                (sum, owner) => sum + (owner.ownershipPercent ?? 0),
                this.parent.applicantPercentageShare ?? 0
              )
              return ownershipTotal <= MAX_SHARE
            },
          }),
      otherwise: (schema) => schema,
    }),
})

export const beneficialOwnersSchema = object({
  individualOwnership: individualOwnerSchema,
  otherIndividualOwnership: individualOwnerOtherSchema,
  otherEntityOwnership: otherEntityOwnershipSchema,
  confirmNoBeneficialOwners: object({}),
  beneficialOwnersTable: beneficialOwnersTableSchema,
  beneficialOwnerName: beneficialOwnerNameSchema,
  beneficialOwnerDetails: beneficialOwnerDetailsSchema,
  beneficialOwnerTaxInfo: beneficialOwnerTaxInfoSchema,
  beneficialOwnerAddress: beneficialOwnerAddressSchema,
})

/* v8 ignore start -- @preserve */
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  const validBeneficialOwners = {
    individualOwnership: {
      individualHasShare: true,
      individualPercentageShare: 15,
    },
    otherIndividualOwnership: { individualOtherHasShare: false },
    otherEntityOwnership: { entityOtherHasShare: true },
    beneficialOwnersTable: {
      applicantPercentageShare: 0,
      shouldShowOwnerForm: false,
      beneficialOwners: [],
    },
    beneficialOwnerName: {
      firstName: "John",
      lastName: "Doe",
    },
    beneficialOwnerDetails: {
      email: "john@example.com",
      birthDate: "1980-01-01",
      ownershipPercent: 25,
      phoneNumber: {
        countryCode: "USA",
        phoneNumber: "2164020202",
      },
    },
    beneficialOwnerTaxInfo: {
      citizenshipCountry: "USA",
      residesInUS: true,
      taxNumber: "123456789",
    },
    beneficialOwnerAddress: {
      address: {
        streetOne: "123 Main St",
        city: "New York",
        stateRegion: "NY",
        postalCode: "10001",
        country: "USA",
        availableSubdivisions: [],
      },
    },
  }

  describe("beneficialOwnersSchema", () => {
    it("returns true for valid data", () => {
      expect(beneficialOwnersSchema.isValidSync(validBeneficialOwners)).toBe(
        true
      )
    })

    it("doesn't require individualPercentageShare when individualHasShare is false", () => {
      expect(
        beneficialOwnersSchema.isValidSync({
          ...validBeneficialOwners,
          individualOwnership: {
            ...validBeneficialOwners.individualOwnership,
            individualHasShare: false,
            individualPercentageShare: null,
          },
        })
      ).toBe(true)
    })

    it("requires individualHasShare", () => {
      expect(
        beneficialOwnersSchema.isValidSync({
          ...validBeneficialOwners,
          individualOwnership: {
            ...validBeneficialOwners.individualOwnership,
            individualHasShare: null,
            individualPercentageShare: null,
          },
        })
      ).toBe(false)
    })

    it("requires individualOtherHasShare", () => {
      expect(
        beneficialOwnersSchema.isValidSync({
          ...validBeneficialOwners,
          otherIndividualOwnership: {
            ...validBeneficialOwners.otherIndividualOwnership,
            individualOtherHasShare: null,
          },
        })
      ).toBe(false)
    })

    it("requires entityOtherHasShare", () => {
      expect(
        beneficialOwnersSchema.isValidSync({
          ...validBeneficialOwners,
          otherEntityOwnership: {
            ...validBeneficialOwners.otherEntityOwnership,
            entityOtherHasShare: null,
          },
        })
      ).toBe(false)
    })
  })

  describe("beneficialOwnerNameSchema", () => {
    it("requires firstName", async () => {
      await expect(
        beneficialOwnerNameSchema.validate({ lastName: "Doe" })
      ).rejects.toThrow()
    })

    it("requires lastName", async () => {
      await expect(
        beneficialOwnerNameSchema.validate({ firstName: "John" })
      ).rejects.toThrow()
    })

    it("accepts valid name with middleName", async () => {
      const data = { firstName: "John", middleName: "M", lastName: "Doe" }
      await expect(beneficialOwnerNameSchema.validate(data)).resolves.toEqual(
        data
      )
    })

    it("accepts valid name without middleName", async () => {
      const data = { firstName: "John", lastName: "Doe" }
      await expect(
        beneficialOwnerNameSchema.validate(data)
      ).resolves.toBeDefined()
    })
  })

  describe("beneficialOwnerDetailsSchema", () => {
    it("requires email", async () => {
      await expect(
        beneficialOwnerDetailsSchema.validate({
          birthDate: "1980-01-01",
          ownershipPercent: 25,
        })
      ).rejects.toThrow()
    })

    it("requires birthDate", async () => {
      await expect(
        beneficialOwnerDetailsSchema.validate({
          email: "test@example.com",
          ownershipPercent: 25,
        })
      ).rejects.toThrow()
    })

    it("requires ownershipPercent", async () => {
      await expect(
        beneficialOwnerDetailsSchema.validate({
          email: "test@example.com",
          birthDate: "1980-01-01",
        })
      ).rejects.toThrow()
    })

    it("validates ownershipPercent range", async () => {
      await expect(
        beneficialOwnerDetailsSchema.validate({
          email: "test@example.com",
          birthDate: "1980-01-01",
          ownershipPercent: 101,
        })
      ).rejects.toThrow()

      await expect(
        beneficialOwnerDetailsSchema.validate({
          email: "test@example.com",
          birthDate: "1980-01-01",
          ownershipPercent: -1,
        })
      ).rejects.toThrow()
    })

    it("accepts valid details", () => {
      const data = {
        email: "test@example.com",
        birthDate: "1980-01-01",
        ownershipPercent: 25,
        phoneNumber: {
          countryCode: "USA",
          phoneNumber: "2164020202",
        },
      }
      expect(beneficialOwnerDetailsSchema.validateSync(data)).toEqual(data)
    })

    it("accepts valid details without phoneNumber", () => {
      const data = {
        email: "test@example.com",
        birthDate: "1980-01-01",
        ownershipPercent: 25,
      }
      expect(beneficialOwnerDetailsSchema.validateSync(data)).toBeDefined()
    })

    it("validates phoneNumber format when provided", () => {
      expect(() =>
        beneficialOwnerDetailsSchema.validateSync({
          email: "test@example.com",
          birthDate: "1980-01-01",
          ownershipPercent: 25,
          phoneNumber: {
            countryCode: "USA",
            phoneNumber: "invalid",
          },
        })
      ).toThrow()
    })

    it("accepts valid phoneNumber when provided", () => {
      const data = {
        email: "test@example.com",
        birthDate: "1980-01-01",
        ownershipPercent: 25,
        phoneNumber: {
          countryCode: "USA",
          phoneNumber: "3123074492",
        },
      }
      expect(beneficialOwnerDetailsSchema.validateSync(data)).toEqual(data)
    })
  })

  describe("beneficialOwnerTaxInfoSchema", () => {
    describe("US resident", () => {
      it("validates SSN with 9 digits", async () => {
        const data = {
          citizenshipCountry: "USA",
          residesInUS: true,
          taxNumber: "123456789",
        }
        await expect(
          beneficialOwnerTaxInfoSchema.validate(data)
        ).resolves.toEqual(data)
      })

      it("strips dashes from SSN", async () => {
        const result = await beneficialOwnerTaxInfoSchema.validate({
          citizenshipCountry: "USA",
          residesInUS: true,
          taxNumber: "123-45-6789",
        })
        expect(result.taxNumber).toBe("123456789")
      })

      it("rejects SSN with incorrect length", async () => {
        await expect(
          beneficialOwnerTaxInfoSchema.validate({
            citizenshipCountry: "USA",
            residesInUS: true,
            taxNumber: "12345678",
          })
        ).rejects.toThrow()
      })

      it("requires taxNumber for US residents", async () => {
        await expect(
          beneficialOwnerTaxInfoSchema.validate({
            citizenshipCountry: "USA",
            residesInUS: true,
          })
        ).rejects.toThrow()
      })
    })

    describe("Foreign resident", () => {
      it("accepts taxNumber up to 40 characters", async () => {
        const data = {
          citizenshipCountry: "FRA",
          residesInUS: false,
          taxNumber: "FOREIGN-TAX-ID-12345",
        }
        await expect(
          beneficialOwnerTaxInfoSchema.validate(data)
        ).resolves.toEqual(data)
      })

      it("rejects taxNumber over 40 characters", async () => {
        await expect(
          beneficialOwnerTaxInfoSchema.validate({
            citizenshipCountry: "FRA",
            residesInUS: false,
            taxNumber: "A".repeat(41),
          })
        ).rejects.toThrow()
      })

      it("requires taxNumber for foreign residents", async () => {
        await expect(
          beneficialOwnerTaxInfoSchema.validate({
            citizenshipCountry: "FRA",
            residesInUS: false,
          })
        ).rejects.toThrow()
      })
    })
  })

  describe("beneficialOwnersTableSchema", () => {
    it("rejects total ownership >100%", async () => {
      await expect(
        beneficialOwnersTableSchema.validate({
          applicantPercentageShare: 50,
          individualHasShare: true,
          shouldShowOwnerForm: false,
          beneficialOwners: [],
        })
      ).resolves.toBeDefined()

      expect(
        beneficialOwnersTableSchema.isValidSync({
          applicantPercentageShare: 50,
          individualHasShare: true,
          shouldShowOwnerForm: false,
          beneficialOwners: [
            { ownershipPercent: 40 },
            { ownershipPercent: 30 },
          ],
        })
      ).toBe(false)
    })

    it("includes applicant percentage in total", () => {
      expect(
        beneficialOwnersTableSchema.isValidSync({
          applicantPercentageShare: 60,
          individualHasShare: true,
          shouldShowOwnerForm: false,
          beneficialOwners: [{ ownershipPercent: 41 }],
        })
      ).toBe(false)
    })

    it("requires applicant percentage >= 1 when individualHasShare is true", () => {
      expect(
        beneficialOwnersTableSchema.isValidSync({
          applicantPercentageShare: 0,
          individualHasShare: true,
          shouldShowOwnerForm: false,
          beneficialOwners: [],
        })
      ).toBe(false)

      expect(
        beneficialOwnersTableSchema.isValidSync({
          applicantPercentageShare: 1,
          individualHasShare: true,
          shouldShowOwnerForm: false,
          beneficialOwners: [],
        })
      ).toBe(true)
    })

    it("skips applicant min validation when individualHasShare is false", () => {
      expect(
        beneficialOwnersTableSchema.isValidSync({
          applicantPercentageShare: 0,
          individualHasShare: false,
          shouldShowOwnerForm: false,
          beneficialOwners: [],
        })
      ).toBe(true)
    })
  })
}
